// frontend/src/components/LOS/Niyogin/NiyoginKFS.tsx
//
// Niyogin KFS + Loan Agreement — Key Facts Statement
// ====================================================
// Flow:
//  1. On mount, call POST /api/niyogin/kfs/generate/
//     → Backend fills the Intellend-Phonepe-CLA+KFS.docx template with user data,
//       converts to PDF via LibreOffice, uploads to S3/local, returns a presigned URL.
//  2. Show a summary of key loan facts (loan amount, tenure, ROI, EMI, fees).
//  3. Show a "View KFS Document" link/button that opens the PDF in a new tab.
//  4. User reads the KFS, then clicks "I've Read & Accept the KFS — Continue".
//  5. onDone() is called → journey advances to the next step (CreateLoan).
//
// The generated PDF URL is also stored in VerificationMedia and will be
// automatically picked up by trigger_niyogin_create_loan as the KFS + agreement URL.
//
// On generation failure the user sees the error + a Retry button.

import React, { useEffect, useRef, useState } from "react";
import axios from "@/lib/axios";
import { API_BASE_URL } from "@/config";
import JourneyHeader from "@/components/JourneyHeader";

interface Props {
  value?: any;
  onChange?: (val: any) => void;
  disabled?: boolean;
  registerSubmit?: (handler: () => Promise<boolean>) => void;
  onDone?: () => void;
  /** When true (builder/preview mode) skip all API calls and show a static placeholder */
  isBuilderMode?: boolean;
}

interface KFSData {
  kfs_url: string;
  media_id: number;
  object_key: string;
}

type Phase = "generating" | "ready" | "error";

const NiyoginKFS: React.FC<Props> = ({ onDone, isBuilderMode = false }) => {
  const startedRef = useRef(false);

  const [phase, setPhase] = useState<Phase>("generating");
  const [kfsData, setKfsData] = useState<KFSData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [accepted, setAccepted] = useState(false);

  // ── Generate KFS on mount ──────────────────────────────────────────────────
  const generateKFS = async () => {
    setPhase("generating");
    setError(null);
    try {
      const res = await axios.post(`${API_BASE_URL}/api/niyogin/kfs/generate/`, {});

      if (res.data?.success === false) {
        throw new Error(res.data?.error || "KFS generation failed.");
      }

      setKfsData({
        kfs_url:    res.data.kfs_url    || "",
        media_id:   res.data.media_id   || 0,
        object_key: res.data.object_key || "",
      });
      setPhase("ready");
    } catch (err: any) {
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        (err instanceof Error ? err.message : String(err));
      console.error("[NiyoginKFS] Generation error:", msg, err?.response?.data);
      setError(msg);
      setPhase("error");
    }
  };

  useEffect(() => {
    if (isBuilderMode) return;
    if (startedRef.current) return;
    startedRef.current = true;
    generateKFS();
  }, [isBuilderMode]);

  // ── Retry ──────────────────────────────────────────────────────────────────
  const handleRetry = () => {
    startedRef.current = false;
    setAccepted(false);
    startedRef.current = true;
    generateKFS();
  };

  // ── Accept & Continue ──────────────────────────────────────────────────────
  const handleAccept = () => {
    setAccepted(true);
    onDone?.();
  };

  // ── Builder/preview mode: static placeholder ──────────────────────────────
  if (isBuilderMode) {
    return (
      <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 p-4 text-center text-sm text-gray-400">
        <div className="text-2xl mb-1">📄</div>
        <p className="font-semibold text-gray-500">Niyogin: KFS + Loan Agreement</p>
        <p className="text-xs mt-1">Generates KFS PDF, shows viewer link, requires acceptance before advancing.</p>
      </div>
    );
  }

  // ── Render: Generating ─────────────────────────────────────────────────────
  if (phase === "generating") {
    return (
      <div className="min-h-screen bg-intellend-white">
        <JourneyHeader />
        <main className="container mx-auto px-4 py-8 max-w-md">
          <div className="bg-white shadow-md rounded-lg p-8 text-center space-y-4">
            <div className="text-5xl">📄</div>
            <h2 className="text-xl font-bold text-gray-800">Generating Your KFS…</h2>
            <p className="text-gray-500 text-sm">
              Preparing your Key Facts Statement and Loan Agreement document. This may take a few
              seconds.
            </p>
            <div className="flex justify-center">
              <svg className="animate-spin h-8 w-8 text-intellend-green" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
              </svg>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // ── Render: Error ──────────────────────────────────────────────────────────
  if (phase === "error") {
    return (
      <div className="min-h-screen bg-intellend-white">
        <JourneyHeader />
        <main className="container mx-auto px-4 py-8 max-w-md">
          <div className="bg-white shadow-md rounded-lg p-8 text-center space-y-4">
            <div className="text-5xl">⚠️</div>
            <h2 className="text-xl font-bold text-red-600">KFS Generation Failed</h2>
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-left space-y-1">
              <p className="text-sm font-semibold text-red-700">❌ Error</p>
              <p className="text-sm text-red-600">{error}</p>
              <p className="text-xs text-red-500">Please retry or contact support.</p>
            </div>
            <button
              onClick={handleRetry}
              className="w-full py-3 px-6 bg-intellend-green text-white font-semibold rounded-lg hover:bg-intellend-green/90 transition-colors"
            >
              Retry
            </button>
          </div>
        </main>
      </div>
    );
  }

  // ── Render: Ready ──────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-intellend-white">
      <JourneyHeader />
      <main className="container mx-auto px-4 py-8 max-w-md">
        <div className="bg-white shadow-md rounded-lg p-8 space-y-6">
          {/* Header */}
          <div className="text-center space-y-2">
            <div className="text-5xl">📋</div>
            <h2 className="text-xl font-bold text-gray-800">Key Facts Statement</h2>
            <p className="text-sm text-gray-500">
              Please review your loan agreement and Key Facts Statement (KFS) before proceeding.
            </p>
          </div>

          {/* KFS Document Link */}
          {kfsData?.kfs_url ? (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
              <div className="flex items-center gap-2">
                <span className="text-2xl">📑</span>
                <div>
                  <p className="text-sm font-semibold text-blue-800">
                    KFS + Loan Agreement Document
                  </p>
                  <p className="text-xs text-blue-600">
                    Generated and ready for your review
                  </p>
                </div>
              </div>
              <a
                href={kfsData.kfs_url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 w-full py-2 px-4 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition-colors"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
                View KFS Document (PDF)
              </a>
            </div>
          ) : (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-sm text-yellow-700">
              ⚠️ Document URL not available. Please retry.
            </div>
          )}

          {/* What's in the KFS */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-2">
            <p className="text-sm font-semibold text-gray-700">This document contains:</p>
            <ul className="text-sm text-gray-600 space-y-1 list-disc list-inside">
              <li>Loan amount, tenure and interest rate</li>
              <li>Equated Daily Instalment (EDI) details</li>
              <li>Processing fees and other charges</li>
              <li>Annual Percentage Rate (APR)</li>
              <li>Repayment schedule and due dates</li>
              <li>Terms and conditions of the loan agreement</li>
              <li>Grievance redressal mechanism</li>
            </ul>
          </div>

          {/* Acceptance checkbox */}
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={accepted}
              onChange={(e) => setAccepted(e.target.checked)}
              className="mt-1 h-4 w-4 rounded border-gray-300 text-intellend-green focus:ring-intellend-green"
            />
            <span className="text-sm text-gray-600">
              I have read and understood the Key Facts Statement and Loan Agreement. I agree to the
              terms and conditions of this loan.
            </span>
          </label>

          {/* Primary CTA */}
          <button
            onClick={handleAccept}
            disabled={!accepted || !kfsData?.kfs_url}
            className="w-full py-3 px-6 bg-intellend-green text-white font-semibold rounded-lg hover:bg-intellend-green/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            ✅ I've Read & Accept the KFS — Continue
          </button>

          {/* Regenerate link */}
          <button
            onClick={handleRetry}
            className="w-full py-2 text-xs text-gray-400 underline hover:text-gray-600 transition-colors"
          >
            Regenerate Document
          </button>
        </div>
      </main>
    </div>
  );
};

export default NiyoginKFS;
